import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Lock, MapPinned, Users } from 'lucide-react';
import {
  xamplinApi,
  entityIdOf,
  deriveEventStatus,
  PURPOSE_BY_ID,
  type XpStructureGroup,
  type XpStructureEvent,
  type XpStructureStation,
} from '@/lib/api/xamplin';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { cn } from '@/lib/cn';
import { EventDetailDrawer } from './EventDetailDrawer';
import { ReadinessMeter, PersonaPanel } from './PlannerReadiness';

type Mode = 'plan' | 'track' | 'measure';

const MODE_COLOR: Record<Mode, string> = {
  plan: 'var(--color-xk-accent)',
  track: '#4dd28b',
  measure: '#5aa9ff',
};

/**
 * Project planner (build-v2 F4). Reads `/xp-project/:id/structure`, renders the
 * Group → Event → Station outline with a Plan/Track/Measure toggle, a mode-aware
 * KPI strip and inline create flows. The trial-anchor station is TRY-locked by
 * the backend (F1 M3) and shown with a lock badge.
 */
export function ProjectPlannerPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? Number(id) : 0;
  const qc = useQueryClient();

  const structureQ = useQuery({
    queryKey: queryKeys.xamplin.projectStructure(projectId),
    queryFn: () => xamplinApi.projects.structure(projectId),
    enabled: projectId > 0,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(),
    queryFn: () => xamplinApi.projects.list(),
  });
  const brandsQ = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });

  const project = projectsQ.data?.items.find((p) => p.id === projectId);
  const companyId = project?.companyId ?? brandsQ.data?.items[0]?.id ?? 0;
  const proj = structureQ.data?.structured?.project;
  const groups = proj?.eventGroups ?? [];
  const demographics = proj?.demographics ?? [];
  const projectName = proj?.name ?? project?.name ?? `Project ${projectId}`;
  const [detailEvent, setDetailEvent] = useState<XpStructureEvent | null>(null);

  const allEvents = useMemo(() => groups.flatMap((g) => g.events ?? []), [groups]);
  const defaultMode: Mode = useMemo(() => {
    if (allEvents.some((e) => deriveEventStatus(e) === 'live')) return 'track';
    const done = allEvents.filter((e) => deriveEventStatus(e) === 'completed').length;
    if (allEvents.length > 0 && done >= allEvents.length / 2) return 'measure';
    return 'plan';
  }, [allEvents]);
  const [mode, setMode] = useState<Mode | null>(null);
  const activeMode = mode ?? defaultMode;

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.xamplin.projectStructure(projectId) });

  const addGroup = useMutation({
    mutationFn: (name: string) =>
      xamplinApi.eventGroups.create({ name, project: projectId, companyId }),
    onSuccess: invalidate,
  });

  const stationCount = allEvents.reduce((s, e) => s + (e.stations?.length ?? 0), 0);
  const liveCount = allEvents.filter((e) => deriveEventStatus(e) === 'live').length;
  const doneCount = allEvents.filter((e) => deriveEventStatus(e) === 'completed').length;

  return (
    <div className="page flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/projects"
          className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            {projectName}
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {groups.length} groups · {allEvents.length} events · {stationCount} stations
          </p>
        </div>
        <Link
          to={`/projects/${projectId}/staffing`}
          className="mono flex flex-none items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-text-muted)] transition-colors hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
        >
          <Users className="h-3.5 w-3.5" /> Staffing
        </Link>
        <Link
          to={`/projects/${projectId}/route`}
          className="mono flex flex-none items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-text-muted)] transition-colors hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
        >
          <MapPinned className="h-3.5 w-3.5" /> Route
        </Link>
        <ModeToggle mode={activeMode} setMode={setMode} />
      </div>

      {/* KPI strip (mode-aware) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {activeMode === 'plan' && (
          <>
            <Kpi label="Objective" value={project?.marketingObjectivePrimary || 'Trial'} />
            <Kpi label="Event groups" value={String(groups.length)} />
            <Kpi label="Events" value={String(allEvents.length)} />
            <Kpi label="Stations" value={String(stationCount)} />
          </>
        )}
        {activeMode === 'track' && (
          <>
            <Kpi label="Live now" value={String(liveCount)} tone="#4dd28b" />
            <Kpi label="Events" value={String(allEvents.length)} />
            <Kpi label="Stations" value={String(stationCount)} />
            <Kpi label="Samples" value="—" hint="F8" />
          </>
        )}
        {activeMode === 'measure' && (
          <>
            <Kpi label="Completed" value={String(doneCount)} tone="#5aa9ff" />
            <Kpi label="MENT avg" value="—" hint="F8" tone="#5aa9ff" />
            <Kpi label="Response rate" value="—" hint="F8" tone="#5aa9ff" />
            <Kpi label="Demo match" value="—" hint="F8" tone="#5aa9ff" />
          </>
        )}
      </div>

      {/* Launch readiness + personas (build-v2 G5/G6) */}
      {!structureQ.isLoading && !structureQ.isError && groups.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ReadinessMeter events={allEvents} />
          <PersonaPanel demographics={demographics} />
        </div>
      )}

      {/* Loading / error */}
      {structureQ.isLoading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando estructura…</div>
      )}
      {structureQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudo cargar la estructura del proyecto.
        </div>
      )}

      {/* Roster — drag a member onto any event to staff it (G5/planner parity) */}
      {!structureQ.isLoading && !structureQ.isError && groups.length > 0 && activeMode !== 'measure' && (
        <RosterStrip events={allEvents} />
      )}

      {/* Outline */}
      {!structureQ.isLoading && !structureQ.isError && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
              Event Groups
            </h2>
            <span className="mono text-xs text-[var(--color-xk-text-subtle)]">{groups.length}</span>
          </div>

          {groups.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[var(--color-xk-border)] px-6 py-8 text-center text-sm text-[var(--color-xk-text-muted)]">
              Sin grupos todavía. Crea el primero para empezar a planear.
            </div>
          )}

          {groups.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              mode={activeMode}
              projectId={projectId}
              companyId={companyId}
              onChanged={invalidate}
              onOpenDetail={setDetailEvent}
            />
          ))}

          <AddInline
            placeholder="Nuevo event group (mercado)…"
            busy={addGroup.isPending}
            onAdd={(name) => addGroup.mutate(name)}
          />
        </section>
      )}

      {detailEvent && (
        <EventDetailDrawer event={detailEvent} projectId={projectId} onClose={() => setDetailEvent(null)} />
      )}
    </div>
  );
}

function ModeToggle({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <div className="flex overflow-hidden rounded-lg border border-[var(--color-xk-border)]">
      {(['plan', 'track', 'measure'] as Mode[]).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => setMode(m)}
          className={cn(
            'px-4 py-1.5 text-sm font-semibold capitalize transition-colors',
            mode === m
              ? 'text-white'
              : 'text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)]',
          )}
          style={mode === m ? { background: MODE_COLOR[m] } : undefined}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function GroupRow({
  group,
  mode,
  projectId,
  companyId,
  onChanged,
  onOpenDetail,
}: {
  group: XpStructureGroup;
  mode: Mode;
  projectId: number;
  companyId: number;
  onChanged: () => void;
  onOpenDetail: (e: XpStructureEvent) => void;
}) {
  const [open, setOpen] = useState(true);
  const qc = useQueryClient();
  const events = group.events ?? [];
  const groupId = entityIdOf(group.id);

  const addEvent = useMutation({
    mutationFn: (name: string) =>
      xamplinApi.events.create({ name, eventGroupId: groupId, companyId }),
    onSuccess: () => {
      onChanged();
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.projectStructure(projectId) });
    },
  });

  return (
    <div className="rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
        )}
        <span className="flex-1 text-sm font-bold text-[var(--color-xk-lavender)]">
          {group.name}
        </span>
        <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">
          {events.length} event{events.length === 1 ? '' : 's'}
        </span>
      </button>

      {/* Branded assets strip — per-group assets by Purpose (planner parity) */}
      <GroupAssetsStrip groupId={groupId} />

      {open && (
        <div className="flex flex-col gap-2 border-t border-[var(--color-xk-border)] p-3">
          {events.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              mode={mode}
              projectId={projectId}
              companyId={companyId}
              onChanged={onChanged}
              onOpenDetail={onOpenDetail}
            />
          ))}
          {events.length === 0 && (
            <div className="mono px-1 py-1 text-[11px] text-[var(--color-xk-text-subtle)]">
              Sin eventos en este grupo.
            </div>
          )}
          <AddInline
            small
            placeholder="Nuevo evento (venue · fecha)…"
            busy={addEvent.isPending}
            onAdd={(name) => addEvent.mutate(name)}
          />
        </div>
      )}
    </div>
  );
}

const STATUS_DOT: Record<string, string> = {
  plan: 'var(--color-xk-accent)',
  live: '#4dd28b',
  completed: '#5aa9ff',
};

function EventRow({
  event,
  mode,
  projectId,
  companyId,
  onChanged,
  onOpenDetail,
}: {
  event: XpStructureEvent;
  mode: Mode;
  projectId: number;
  companyId: number;
  onChanged: () => void;
  onOpenDetail: (e: XpStructureEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const status = deriveEventStatus(event);
  const stations = event.stations ?? [];
  const eventId = entityIdOf(event.id);

  const addStation = useMutation({
    mutationFn: (vars: { name: string; isAnchor: boolean }) =>
      xamplinApi.stations.create({ name: vars.name, eventId, companyId, isAnchor: vars.isAnchor }),
    onSuccess: () => {
      onChanged();
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.projectStructure(projectId) });
    },
  });

  const assignReporter = useMutation({
    mutationFn: (vars: { name: string; email: string }) => {
      const [firstname, ...rest] = vars.name.trim().split(' ');
      return xamplinApi.reporters.assign({
        event: eventId,
        email: vars.email.trim(),
        firstname: firstname || vars.email,
        lastname: rest.join(' ') || '',
      });
    },
    onSuccess: () => {
      onChanged();
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.projectStructure(projectId) });
    },
  });

  const reporters = event.reporters ?? [];
  const full = reporters.length >= 4;
  const baNeeded = Math.max(0, 1 - reporters.length); // need ≥1 BA to not be a gap
  const [dragOver, setDragOver] = useState(false);

  // Drop a roster member onto the event row to assign them (inline staffing).
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (full) return;
    const email = e.dataTransfer.getData('memberEmail');
    const name = e.dataTransfer.getData('memberName') || email;
    if (!email) return;
    if (reporters.some((r) => (r.email ?? r.name) === email)) return;
    assignReporter.mutate({ name, email });
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-[var(--color-xk-bg)] transition-colors',
        dragOver && !full ? 'border-dashed border-[var(--color-xk-accent)]' : 'border-[var(--color-xk-border)]',
      )}
      onDragOver={(e) => {
        if (!full) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex w-full items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <span
            className="h-2 w-2 flex-none rounded-full"
            style={{ background: STATUS_DOT[status] }}
            title={status}
          />
          <span className="flex-1 truncate text-sm text-[var(--color-xk-lavender)]">
            {event.name}
          </span>
          <span className="mono text-[10px] uppercase text-[var(--color-xk-text-subtle)]">
            {status}
          </span>
          <span className="mono text-[10px] text-[var(--color-xk-text-muted)]">
            {stations.length} st
          </span>
          <span className="mono text-[10px] text-[var(--color-xk-text-muted)]">
            👤 {reporters.length}
          </span>
        </button>
        {status !== 'completed' && baNeeded > 0 && (
          <span className="mono flex-none rounded-full border border-[#f4b740]/50 bg-[#f4b740]/10 px-2 py-0.5 text-[9px] font-bold text-[#f4b740]">
            {dragOver ? 'suelta aquí' : `${baNeeded} BA needed · arrastra`}
          </span>
        )}
        <button
          type="button"
          onClick={() => onOpenDetail(event)}
          className="mono flex-none rounded-lg border border-[var(--color-xk-border)] px-2 py-1 text-[10px] text-[var(--color-xk-text-muted)] transition-colors hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
        >
          Detalles
        </button>
        {mode === 'measure' && (
          <Link
            to={`/projects/${projectId}/events/${eventId}`}
            className="mono flex-none rounded-lg border border-[#5aa9ff] px-2 py-1 text-[10px] text-[#5aa9ff] transition-colors hover:bg-[#5aa9ff]/10"
          >
            Measure →
          </Link>
        )}
      </div>

      {open && (
        <div className="flex flex-col gap-1.5 border-t border-[var(--color-xk-border)] p-2.5">
          {stations.map((s) => (
            <StationRow key={s.id} station={s} />
          ))}
          {stations.length === 0 && (
            <div className="mono px-1 text-[10px] text-[var(--color-xk-text-subtle)]">
              Sin stations.
            </div>
          )}
          <div className="flex gap-2">
            <AddInline
              small
              placeholder="Nueva station…"
              busy={addStation.isPending}
              onAdd={(name) => addStation.mutate({ name, isAnchor: false })}
            />
            <button
              type="button"
              disabled={addStation.isPending}
              onClick={() => {
                if (addStation.isPending) return;
                addStation.mutate({ name: 'Tasting Bar', isAnchor: true });
              }}
              title="Crea la trial-anchor station (se bloquea a TRY)"
              className="mono flex flex-none items-center gap-1 rounded-lg border border-[#4dd28b] px-2 py-1 text-[10px] text-[#4dd28b] hover:bg-[#4dd28b]/10"
            >
              <Lock className="h-3 w-3" /> Anchor (TRY)
            </button>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {(event.reporters ?? []).map((r) => (
              <span
                key={r.id}
                className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]"
              >
                👤 {r.name || r.email}
                {r.reporterType ? ` · ${r.reporterType}` : ''}
              </span>
            ))}
            <ReporterAssignInline
              busy={assignReporter.isPending}
              full={(event.reporters ?? []).length >= 4}
              onAssign={(name, email) => assignReporter.mutate({ name, email })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact "assign reporter" affordance for an event row (build-v2 F5). Expands
 * to a name+email mini-form; enforces the ≤4/event cap client-side too. */
function ReporterAssignInline({
  busy,
  full,
  onAssign,
}: {
  busy?: boolean;
  full?: boolean;
  onAssign: (name: string, email: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!busy) submittingRef.current = false;
  }, [busy]);

  if (full) {
    return (
      <span className="mono text-[10px] text-[var(--color-xk-text-subtle)]">máx 4 reporters</span>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mono rounded-full border border-dashed border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)] hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
      >
        + reporter
      </button>
    );
  }
  const submit = () => {
    if (busy || submittingRef.current) return;
    if (!email.trim() || !/.+@.+\..+/.test(email)) return;
    submittingRef.current = true;
    onAssign(name || email, email);
    setName('');
    setEmail('');
    setOpen(false);
  };
  return (
    <span className="flex items-center gap-1">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="nombre"
        className="w-20 rounded border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
      />
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder="email"
        className="w-28 rounded border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="mono rounded border border-[var(--color-xk-accent)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--color-xk-accent)] disabled:opacity-40"
      >
        ok
      </button>
    </span>
  );
}

const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff',
  ENGAGE: '#f4b740',
  TRY: '#4dd28b',
  ACT: '#ff5a4f',
};

function StationRow({ station }: { station: XpStructureStation }) {
  const purpose = station.purpose ?? null;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-[var(--color-xk-surface)] px-2.5 py-1.5">
      <span className="flex-1 truncate text-xs text-[var(--color-xk-lavender)]">{station.name}</span>
      {station.isAnchor && (
        <span className="mono flex items-center gap-0.5 text-[9px] text-[#4dd28b]">
          <Lock className="h-2.5 w-2.5" /> anchor
        </span>
      )}
      {purpose && (
        <span
          className="mono rounded-full px-2 py-0.5 text-[9px] font-bold"
          style={{
            color: PURPOSE_COLOR[purpose] ?? 'var(--color-xk-text-muted)',
            border: `1px solid ${PURPOSE_COLOR[purpose] ?? 'var(--color-xk-border)'}`,
          }}
        >
          {purpose}
        </span>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
      <div
        className="truncate font-display text-lg font-extrabold"
        style={{ color: tone ?? 'var(--color-xk-lavender)' }}
      >
        {value}
      </div>
      <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        {label}
        {hint && <span className="ml-1 text-[var(--color-xk-accent)]">{hint}</span>}
      </div>
    </div>
  );
}

const PURPOSE_PILL: Record<string, string> = {
  SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f',
};

/** Draggable roster — distinct reporters across the project. Drag a chip onto
 * an event row to staff it inline (planner parity with the prototype). */
function RosterStrip({ events }: { events: XpStructureEvent[] }) {
  const roster = useMemo(() => {
    const byEmail = new Map<string, { email: string; name: string; type?: string | null }>();
    for (const e of events)
      for (const r of e.reporters ?? []) {
        const email = r.email ?? r.name ?? '';
        if (!email || byEmail.has(email)) continue;
        byEmail.set(email, { email, name: r.name ?? email, type: r.reporterType });
      }
    return [...byEmail.values()];
  }, [events]);
  if (roster.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
      <span className="mono flex-none text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Roster · arrastra a un evento
      </span>
      {roster.slice(0, 14).map((m) => (
        <span
          key={m.email}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('memberEmail', m.email);
            e.dataTransfer.setData('memberName', m.name);
            e.dataTransfer.effectAllowed = 'copy';
          }}
          className="mono flex cursor-grab items-center gap-1 rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-2.5 py-1 text-[11px] text-[var(--color-xk-lavender)] active:cursor-grabbing"
        >
          👤 {m.name}
        </span>
      ))}
      <Link
        to="staffing"
        className="mono ml-auto flex-none rounded-lg border border-[var(--color-xk-border)] px-2 py-1 text-[10px] text-[var(--color-xk-text-muted)] hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
      >
        Roster completo →
      </Link>
    </div>
  );
}

/** Branded-assets strip for a group — Purpose tally + thumbnails + Assign,
 * faithful to the prototype `BrandedAssetsStrip`. Real assets via xp-photo. */
function GroupAssetsStrip({ groupId }: { groupId: number }) {
  const q = useQuery({
    queryKey: queryKeys.xamplin.photoFeed({ groupId, scope: 'group-assets' }),
    queryFn: () => xamplinApi.photos.feed({ groupId, limit: 40 }),
    enabled: groupId > 0,
  });
  const items = q.data?.items ?? [];
  const tally: Record<string, number> = {};
  for (const a of items) {
    const p = a.purposeId ? PURPOSE_BY_ID[a.purposeId] : null;
    if (p) tally[p] = (tally[p] ?? 0) + 1;
  }
  return (
    <div className="scrollbar-none flex items-center gap-2 overflow-x-auto border-t border-[var(--color-xk-border)] bg-[var(--color-accent-04)] px-3 py-2">
      <span className="mono flex-none text-[9px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Branded assets
      </span>
      {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) =>
        tally[p] ? (
          <span
            key={p}
            className="mono flex-none rounded-full px-2 py-0.5 text-[9px] font-bold text-white"
            style={{ background: PURPOSE_PILL[p] }}
          >
            {p} ×{tally[p]}
          </span>
        ) : null,
      )}
      {items.length === 0 && (
        <span className="mono flex-none text-[10px] italic text-[var(--color-xk-text-subtle)]">
          {q.isLoading ? 'cargando…' : 'None assigned'}
        </span>
      )}
      <div className="flex flex-none gap-1">
        {items.slice(0, 8).map((a) => (
          <img
            key={a.id}
            src={a.image}
            alt=""
            loading="lazy"
            title={a.purposeId ? PURPOSE_BY_ID[a.purposeId] : ''}
            className="h-9 w-9 flex-none rounded-md border border-[var(--color-xk-border)] object-cover"
          />
        ))}
      </div>
      <Link
        to="/assets"
        className="mono ml-auto flex-none rounded-lg border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)] hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
      >
        + Assign
      </Link>
    </div>
  );
}

function AddInline({
  placeholder,
  onAdd,
  busy,
  small,
}: {
  placeholder: string;
  onAdd: (name: string) => void;
  busy?: boolean;
  small?: boolean;
}) {
  const [name, setName] = useState('');
  // Re-entrancy guard: block a second submit (Enter + click, double Enter, or a
  // synchronous double-call before `busy` flips) until the in-flight create
  // settles. Cleared when `busy` returns to false.
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!busy) submittingRef.current = false;
  }, [busy]);
  const submit = () => {
    const v = name.trim();
    if (!v || busy || submittingRef.current) return;
    submittingRef.current = true;
    onAdd(v);
    setName('');
  };
  return (
    <div className="flex flex-1 items-center gap-2">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder={placeholder}
        className={cn(
          'flex-1 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 text-[var(--color-xk-lavender)] outline-none placeholder:text-[var(--color-xk-text-subtle)] focus-visible:border-[var(--color-xk-accent)]',
          small ? 'py-1.5 text-xs' : 'py-2 text-sm',
        )}
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || !name.trim()}
        className={cn(
          'flex flex-none items-center gap-1 rounded-lg border border-[var(--color-xk-accent)] font-semibold text-[var(--color-xk-accent)] transition-colors hover:bg-[var(--color-accent-10)] disabled:opacity-40',
          small ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm',
        )}
      >
        <Plus className={small ? 'h-3 w-3' : 'h-4 w-4'} /> Add
      </button>
    </div>
  );
}
